import { useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Credenza, CredenzaTitle } from '@/components/ui/credenza'
import DynamicCredenzaContent from '@/components/ui/credenza-content'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import RenderIf from '@/components/ui/render-if'
import { Separator } from '@/components/ui/separator'
import { Text } from '@/components/ui/text'
import { EClaimStatus, ICreateClaimRequestPayload, useMutationClaimReferralReward } from '@/data/referral'
import { useToast } from '@/hooks'
import { CommonXCircle } from '@/icons'
import { CommonUsdt } from '@/icons/icons/CommonUsdt'
import { cn } from '@/lib'
import { useClaimAmount, useReferralActions, useReferralWallet } from '@/store'
import {
  createRewardClaimMessage,
  formatTokenAmount,
  getChainType,
  isEvmChain,
  SUPPORTED_NETWORK,
  validateRecipientAddress,
} from '@/utils'
import { WalletContextType } from '@/wallets'

import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const useClaimSignature = (referralWallet: WalletContextType | undefined, t: TFunction<'translation', undefined>) => {
  return useCallback(async () => {
    const signerAddress = referralWallet?.address
    const timestamp = Date.now()
    const message = createRewardClaimMessage(signerAddress || '', timestamp)
    const signature = await referralWallet?.wallet?.signMessage(message, referralWallet?.network || '', {
      btcSignMessageType: 'ecdsa',
    })
    const publicKey = await referralWallet?.wallet?.getCompressedPublicKey(referralWallet?.networkInfo?.networkId || '')
    const chainType = getChainType(referralWallet?.networkInfo?.networkId || '')
    if (!chainType) {
      throw new Error(t('error.INVALID_NETWORK_TYPE'))
    }
    return { signature, publicKey, chainType, timestamp, signerAddress }
  }, [referralWallet, t])
}

export const ClaimReward = () => {
  const { setOpenClaimModal } = useReferralActions()
  const claimAmount = useClaimAmount()
  const [isPending, setIsPending] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null)
  const referralWallet = useReferralWallet()
  const { mutateAsync: claimReferralReward } = useMutationClaimReferralReward()
  const toast = useToast()
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      inputAddress: '',
    },
  })
  const errorMessage = errors?.inputAddress?.message
  const { t } = useTranslation()
  const getSignature = useClaimSignature(referralWallet, t)

  const handleRadioClick = (value: string) => {
    setSelectedAddress(selectedAddress === value ? null : value)
  }

  const onSubmit = async () => {
    try {
      if (isPending) return
      setIsPending(true)
      const inputAddress = getValues('inputAddress')
      const receiveAddress = selectedAddress ? selectedAddress : inputAddress
      const { signature, publicKey, chainType, timestamp, signerAddress } = await getSignature()
      const payload: ICreateClaimRequestPayload = {
        walletAddress: signerAddress || '',
        publicKey: publicKey || '',
        chainType: chainType,
        signature: signature || '',
        timestamp: timestamp,
        receiveAddress: receiveAddress,
      }
      const data = await claimReferralReward(payload)
      if (data?.status === EClaimStatus.REJECTED) throw new Error(t('title.CLAIM_REWARD_REJECTED'))
      toast.toast({
        title: t('button.CLAIM_REWARD'),
        description: t('title.CLAIM_REWARD_SUBMITTED'),
      })
      setOpenClaimModal(false)
    } catch (error: any) {
      console.error('claim reward error', error)
      toast.toast({
        title: t('error.ERROR'),
        description: error.message,
        variant: 'destructive',
      })
    } finally {
      setIsPending(false)
    }
  }
  const inputAddressValue = watch('inputAddress')
  const isDisabledClaimButton = !!errorMessage || (!selectedAddress && !inputAddressValue)
  return (
    <Credenza open={true} onOpenChange={setOpenClaimModal}>
      <DynamicCredenzaContent style={{ maxHeight: 'unset' }} open={false} className='py-6 max-w-lg px-6 '>
        <CredenzaTitle className='text-center'>
          <Text>{t('title.REWARD_TO_CLAIM')}</Text>
        </CredenzaTitle>
        <div>
          <Text className='text-center flex items-center justify-center gap-4' size={36}>
            {formatTokenAmount(claimAmount, 'USDT')} USDT
            <CommonUsdt className='size-10 text-green-100' />
          </Text>
          <Separator className='mt-8 mb-6' />
          <form className='space-y-5 px-[1px]' onSubmit={handleSubmit(onSubmit)}>
            <RenderIf condition={isEvmChain(referralWallet?.networkInfo?.networkId)}>
              <>
                <Text as='p'>{t('title.USE_CONNECTED_WALLET_ADDRESS')}</Text>
                <RadioGroup onValueChange={setSelectedAddress}>
                  <div
                    className={cn(
                      'flex items-center justify-between bg-linear-white-20-to-12 rounded-sm px-3 md:px-6 py-3 transition-all duration-300',
                      {
                        'ring-[1px] ring-main-100 bg-main-8': selectedAddress,
                      }
                    )}
                  >
                    <label htmlFor='address'>
                      <Text as='span'>{referralWallet?.address}</Text>
                    </label>
                    <RadioGroupItem
                      value={referralWallet?.address || ''}
                      id='address'
                      checked={selectedAddress === referralWallet?.address}
                      onClick={() => handleRadioClick(referralWallet?.address || '')}
                    />
                  </div>
                </RadioGroup>
                <Text as='p'>{t('title.OR_ENTER_YOUR_EVM_ADDRESS')}</Text>
              </>
            </RenderIf>
            <RenderIf condition={!isEvmChain(referralWallet?.networkInfo?.networkId)}>
              <Text as='p'>{t('title.ENTER_YOUR_EVM_ADDRESS')}</Text>
            </RenderIf>
            <div>
              <Input
                size='lg'
                placeholder={t('title.PASTE_ADDRESS')}
                autoComplete='off'
                autoCorrect='off'
                autoFocus={false}
                {...register('inputAddress', {
                  validate: (value) => {
                    if (!!selectedAddress) return true
                    const result = validateRecipientAddress(
                      value,
                      SUPPORTED_NETWORK.ETHEREUM,
                      SUPPORTED_NETWORK.ETHEREUM
                    )
                    return result
                  },
                })}
                disabled={!!selectedAddress}
                suffix={
                  inputAddressValue ? (
                    <CommonXCircle className='size-6 cursor-pointer' onClick={() => setValue('inputAddress', '')} />
                  ) : null
                }
              />
              {errorMessage && !selectedAddress && (
                <Text as='p' size={12} className='text-red-500 mt-2'>
                  {t(errorMessage, {
                    network: SUPPORTED_NETWORK.ETHEREUM,
                  })}
                </Text>
              )}
            </div>
            <Button size='lg' className='w-full' type='submit' disabled={isDisabledClaimButton} isLoading={isPending}>
              {t('button.CLAIM_REWARD')}
            </Button>
          </form>
        </div>
      </DynamicCredenzaContent>
    </Credenza>
  )
}
